import { ArrowLeft, Compass } from "lucide-react";
import { Link } from "react-router";

import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useT } from "@/shared/i18n/useT";
import { MOD_KEY } from "@/shared/lib/platform";
import { Kbd } from "@/shared/ui/Kbd";
import { buttonVariants } from "@/shared/ui/styles";

/** A link that leads nowhere: say so plainly, and offer the way home, to projects or to search. */
export default function NotFoundPage() {
  const t = useT();
  useDocumentTitle(t("errors.notFound.title"));
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-20 text-center motion-safe:animate-rise">
      <div aria-hidden="true" className="relative">
        <p
          dir="ltr"
          className="bg-gradient-to-b from-ink-faint/70 to-ink-faint/10 bg-clip-text text-[112px] leading-none font-semibold tracking-[-0.06em] text-transparent select-none sm:text-[148px]"
        >
          404
        </p>
        <span className="absolute start-1/2 top-1/2 grid size-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-2xl border border-line bg-surface text-brand-strong shadow-md rtl:translate-x-1/2">
          <Compass className="size-6" strokeWidth={1.75} />
        </span>
      </div>
      <h1 className="mt-6 text-[22px] font-semibold tracking-[-0.02em] text-ink">
        {t("errors.notFound.title")}
      </h1>
      <p className="mt-2 max-w-sm text-[15px] leading-relaxed text-ink-muted">
        {t("errors.notFound.body")}
      </p>
      <div className="mt-7 flex flex-wrap items-center justify-center gap-2">
        <Link to="/" className={buttonVariants()}>
          <ArrowLeft className="rtl:-scale-x-100" aria-hidden="true" />
          {t("errors.unexpected.home")}
        </Link>
        <Link to="/projects" className={buttonVariants({ variant: "secondary" })}>
          {t("errors.notFound.home")}
        </Link>
      </div>
      <p className="mt-6 flex items-center gap-1.5 text-[13px] text-ink-subtle">
        <span dir="ltr" className="flex gap-0.5">
          <Kbd>{MOD_KEY}</Kbd>
          <Kbd>K</Kbd>
        </span>
        {t("errors.notFound.search")}
      </p>
    </div>
  );
}
